import { AnalyticsUserEvent } from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { type Href, Redirect, router } from 'expo-router';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { FullScreenSpinner, SafeAreaView, useToastController } from 'smoothie';

import { useUpdateBankingAdoption } from '../../../bankingSwitch/hooks/useUpdateBankingAdoption';
import {
    type BankingAdoption,
    BankingAdoptionTaskStatus,
} from '../../../bankingSwitch/types/BankingAdoption';
import { captureException, track, trackView } from '../../../logger/Logger';
import useGetPinwheelLinkToken from '../../hooks/paycheck-routing/useGetPinwheelLinkToken';
import { selectedAccountForDirectDepositAtom } from '../../state/SpacesAtoms';
import Pinwheel from './Pinwheel';
import type { DirectDepositSwitchAppContext } from './types';

export const DirectDepositSwitch = ({
    context = '',
    source = '',
}: {
    context?: DirectDepositSwitchAppContext | '';
    source?: string;
}) => {
    const toast = useToastController();
    const { mutate: updateBankingAdoption } = useUpdateBankingAdoption();
    const queryClient = useQueryClient();

    const redirectHref: Href =
        context === 'roadmap' ? '/home' : '/home/banking';

    useEffect(() => {
        trackView(`${context}/${source}/DirectDeposit/Pinwheel`);
        track(AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_OPENED, {
            ddAppContext: context,
            ddSource: source,
            ddFlow: 'Pinwheel',
        });
    }, []);

    const selectedAccountForDirectDeposit = useAtomValue(
        selectedAccountForDirectDepositAtom,
    );
    const bankAccountId = selectedAccountForDirectDeposit?.bankAccountId ?? '';

    const {
        data: linkTokenResponse,
        isFetching,
        isError,
        error,
    } = useGetPinwheelLinkToken(
        {
            bankAccountId,
        },
        { enabled: bankAccountId !== '' },
    );

    if (bankAccountId === '') {
        captureException(new Error('No Pinwheel link token from server'));
        toast.show('Unable to perform direct deposit switch');
        return <Redirect href={redirectHref} />;
    }

    if (isFetching) {
        return <FullScreenSpinner />;
    }

    if (isError) {
        captureException(error);
        toast.show('Unable to perform direct deposit switch');
        return <Redirect href={redirectHref} />;
    }

    if (linkTokenResponse == null) {
        captureException(new Error('No Pinwheel link token from server'));
        toast.show('Unable to perform direct deposit switch');
        return <Redirect href={redirectHref} />;
    }

    return (
        <SafeAreaView>
            <Pinwheel
                linkToken={linkTokenResponse.linkToken}
                onExit={() => {
                    track(AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_EXITED, {
                        ddAppContext: context,
                        ddSource: source,
                        ddFlow: 'Pinwheel',
                    });
                    router.replace(redirectHref);
                }}
                onSuccess={(payload) => {
                    // onExit will handle the navigation
                    toast.show('Direct deposit switch successful!');
                    track(AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_SUCCESS, {
                        ddAppContext: context,
                        ddSource: source,
                        ddFlow: 'Pinwheel',
                        ddAccountId: payload.accountId,
                        ddPlatformId: payload.platformId,
                        ddParams: JSON.stringify(payload.params),
                    });
                    updateBankingAdoption(
                        {
                            body: {
                                directDepositStatus:
                                    BankingAdoptionTaskStatus.COMPLETE,
                            },
                        },
                        {
                            onSuccess: (newData: BankingAdoption) => {
                                void queryClient.setQueryData(
                                    ['getBankingAdoption'],
                                    newData,
                                );
                            },
                        },
                    );
                }}
                onError={(payload) => {
                    captureException(
                        new Error('Error in pinwheel flow'),
                        payload,
                    );
                    toast.show(
                        'We were unable to switch your direct deposit. Please try again.',
                    );
                    track(AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_ERROR, {
                        ddAppContext: context,
                        ddSource: source,
                        ddFlow: 'Pinwheel',
                        ddErrorType: payload.type,
                        ddErrorMessage: payload.message,
                        ddErrorCode: payload.code,
                        ddErrorPendingRetry: payload.pendingRetry,
                    });
                }}
            />
        </SafeAreaView>
    );
};
